/**
 * useSEO — per-route head manager for FLOWTALIS.
 *
 * Zero dependencies: mutates <head> directly on mount and again whenever the
 * inputs change. Cleans up on unmount so a route that sets Product schema
 * doesn't leave stale JSON-LD behind after the user navigates away.
 *
 * Handles:
 *   - <title>
 *   - <meta name="description">
 *   - Open Graph (og:title, og:description, og:image, og:url, og:type, og:site_name)
 *   - Twitter Card (twitter:card, twitter:site, twitter:title, twitter:description, twitter:image)
 *   - <link rel="canonical">
 *   - One or more JSON-LD structured-data blocks (Product, BreadcrumbList, etc.)
 *
 * Usage:
 *   useSEO({
 *     title: "Purified Water — FLOWTALIS™",
 *     description: "Enterprise-grade private-label purified water manufacturing.",
 *     canonical: "https://flowtalis.com/products/purified-water",
 *     image: "https://flowtalis.com/og/purified-water.jpg",
 *     jsonLd: [productSchema, breadcrumbSchema],
 *   });
 */
import { useEffect } from "react";

const SITE_NAME = "FLOWTALIS™";
const SITE_BASE = "https://flowtalis.com";
const DEFAULT_IMAGE = `${SITE_BASE}/og-default.jpg`; // baked into public/
const TWITTER_HANDLE = "@flowtalis";

// --- helpers ---------------------------------------------------------------
const setTitle = (t) => { if (t) document.title = t; };

// Upsert a single meta tag. `key` is the attribute we match on (name|property).
// Returns a cleanup function that restores whatever was there before.
const upsertMeta = (key, keyVal, content) => {
  if (!content) return () => {};
  const selector = `meta[${key}="${keyVal}"]`;
  let el = document.head.querySelector(selector);
  const created = !el;
  const previous = el?.getAttribute("content") ?? null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(key, keyVal);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return () => {
    if (created) el.remove();
    else if (previous !== null) el.setAttribute("content", previous);
  };
};

const upsertCanonical = (href) => {
  if (!href) return () => {};
  let el = document.head.querySelector('link[rel="canonical"]');
  const created = !el;
  const previous = el?.getAttribute("href") ?? null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return () => {
    if (created) el.remove();
    else if (previous !== null) el.setAttribute("href", previous);
  };
};

// Inject one or more JSON-LD script blocks. Each block gets a stable
// data-seo-tag so we can find and remove exactly what we added on cleanup.
const injectJsonLd = (blocks, tag) => {
  if (!blocks || !blocks.length) return () => {};
  const nodes = blocks.map((data) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-seo-tag", tag);
    s.textContent = JSON.stringify(data);
    document.head.appendChild(s);
    return s;
  });
  return () => nodes.forEach((n) => n.remove());
};

// Normalize image URL — external OG scrapers (LinkedIn, Slack, X, iMessage)
// require absolute URLs. When a caller passes a relative path we prepend
// SITE_BASE so every share preview resolves correctly.
const absoluteImage = (image) => {
  if (!image) return DEFAULT_IMAGE;
  if (image.startsWith("http://") || image.startsWith("https://")) return image;
  return `${SITE_BASE}${image.startsWith("/") ? "" : "/"}${image}`;
};

// --- hook ------------------------------------------------------------------
export default function useSEO({
  title,
  description,
  canonical,
  image,
  type = "website",
  jsonLd,
} = {}) {
  useEffect(() => {
    const cleanups = [];

    if (title) {
      const prev = document.title;
      setTitle(title);
      cleanups.push(() => { document.title = prev; });
    }

    const ogTitle = title || SITE_NAME;
    const ogDesc = description || "";
    const ogImage = absoluteImage(image);
    const ogUrl = canonical || (typeof window !== "undefined" ? window.location.href : SITE_BASE);

    cleanups.push(upsertMeta("name", "description", description));
    cleanups.push(upsertMeta("property", "og:title", ogTitle));
    cleanups.push(upsertMeta("property", "og:description", ogDesc));
    cleanups.push(upsertMeta("property", "og:image", ogImage));
    cleanups.push(upsertMeta("property", "og:url", ogUrl));
    cleanups.push(upsertMeta("property", "og:type", type));
    cleanups.push(upsertMeta("property", "og:site_name", SITE_NAME));
    cleanups.push(upsertMeta("name", "twitter:card", "summary_large_image"));
    cleanups.push(upsertMeta("name", "twitter:site", TWITTER_HANDLE));
    cleanups.push(upsertMeta("name", "twitter:title", ogTitle));
    cleanups.push(upsertMeta("name", "twitter:description", ogDesc));
    cleanups.push(upsertMeta("name", "twitter:image", ogImage));
    cleanups.push(upsertCanonical(canonical));
    cleanups.push(injectJsonLd(Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []), "route-seo"));

    return () => cleanups.forEach((fn) => fn && fn());
  }, [title, description, canonical, image, type, JSON.stringify(jsonLd)]);
}

// --- schema builders (reusable across pages) ------------------------------
export const productSchema = ({ name, slug, description, image, category }) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name,
  description,
  category,
  brand: { "@type": "Brand", name: SITE_NAME },
  image: absoluteImage(image),
  url: `${SITE_BASE}/products/${slug}`,
  manufacturer: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_BASE,
  },
});

export const breadcrumbSchema = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.label,
    item: c.href?.startsWith("http") ? c.href : `${SITE_BASE}${c.href || ""}`,
  })),
});

export const caseStudySchema = ({ name, slug, description, image, industry }) => ({
  "@context": "https://schema.org",
  "@type": "CreativeWork",
  name,
  description,
  about: industry || "Private-Label Beverage Manufacturing",
  image: absoluteImage(image),
  url: `${SITE_BASE}/portfolio/${slug}`,
  creator: { "@type": "Organization", name: SITE_NAME, url: SITE_BASE },
});
